// frontend/src/components/Layout/configs/customerAccountConfig.ts
// FIXED: Updated with proper booking sub-routes

import {
  Home,
  Calendar,
  MessageSquare,
  Star,
  User,
  CreditCard,
  Settings,
  Heart,
  Clock,
  CheckCircle,
  Search,
  AlertCircle,
} from 'lucide-react';

export interface CustomerAccountNavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  badge?: string;
  description?: string;
  children?: CustomerAccountNavigationItem[];
}

// Updated customer account navigation with booking sub-routes
export const customerAccountNavigation: CustomerAccountNavigationItem[] = [
  {
    name: 'Overview',
    href: '/account',
    icon: Home,
    description: 'Account summary and recent activity'
  },
  {
    name: 'My Bookings',
    href: '/account/bookings',
    icon: Calendar,
    badge: '2',
    description: 'All your service bookings',
    children: [
      {
        name: 'All Bookings',
        href: '/account/bookings',
        icon: Calendar,
        description: 'View all your bookings'
      },
      {
        name: 'Pending',
        href: '/account/bookings/pending',
        icon: Clock,
        badge: '1',
        description: 'Awaiting provider response'
      },
      {
        name: 'Active Services',
        href: '/account/bookings/accepted',
        icon: AlertCircle,
        badge: '1',
        description: 'Services in progress'
      },
      {
        name: 'Completed',
        href: '/account/bookings/completed',
        icon: CheckCircle,
        description: 'Finished services'
      }
    ]
  },
  {
    name: 'Messages',
    href: '/account/messages',
    icon: MessageSquare,
    badge: '1',
    description: 'Chat with service providers'
  },
  {
    name: 'Reviews & Ratings',
    href: '/account/reviews',
    icon: Star,
    description: 'Rate and review completed services'
  },
  {
    name: 'Profile',
    href: '/account/profile',
    icon: User,
    description: 'Personal information and preferences'
  },
  {
    name: 'Payment & Billing',
    href: '/account/payments',
    icon: CreditCard,
    description: 'Payment methods and billing history'
  },
  {
    name: 'Settings',
    href: '/account/settings',
    icon: Settings,
    description: 'Notifications and account preferences'
  }
];

// Quick actions for homepage/dashboard
export const customerQuickActions: CustomerAccountNavigationItem[] = [
  {
    name: 'Book a Service',
    href: '/services',
    icon: Search,
    description: 'Find and book services near you'
  },
  {
    name: 'Track Active Bookings',
    href: '/account/bookings/accepted',
    icon: CheckCircle,
    badge: '1',
    description: 'Check status of your current services'
  },
  {
    name: 'My Favorites',
    href: '/account/favorites',
    icon: Heart,
    description: 'Your saved providers and services'
  }
];

// Helper function to check if a path is active (including sub-routes)
export const isActiveAccountPath = (href: string, currentPath: string): boolean => {
  // Exact match for base account page
  if (href === '/account' && currentPath === '/account') {
    return true;
  }
  
  // For booking routes, check if current path starts with the href
  if (href !== '/account' && currentPath.startsWith(href)) {
    return true;
  }
  
  // Special case for bookings - highlight parent when on sub-routes
  if (href === '/account/bookings' && currentPath.startsWith('/account/bookings')) {
    return true;
  }
  
  return false;
};

// Get current page info for breadcrumbs
export const getAccountPageInfo = (pathname: string) => {
  const routes = [
    { path: '/account', name: 'Account Overview', parent: null },
    { path: '/account/bookings', name: 'My Bookings', parent: 'Account Overview' },
    { path: '/account/bookings/pending', name: 'Pending Bookings', parent: 'My Bookings' },
    { path: '/account/bookings/accepted', name: 'Active Services', parent: 'My Bookings' },
    { path: '/account/bookings/completed', name: 'Completed Services', parent: 'My Bookings' },
    { path: '/account/messages', name: 'Messages', parent: 'Account Overview' },
    { path: '/account/reviews', name: 'Reviews & Ratings', parent: 'Account Overview' },
    { path: '/account/profile', name: 'Profile', parent: 'Account Overview' },
    { path: '/account/payments', name: 'Payment & Billing', parent: 'Account Overview' },
    { path: '/account/settings', name: 'Settings', parent: 'Account Overview' },
  ];

  const currentRoute = routes.find(route => route.path === pathname);
  return currentRoute || { path: pathname, name: 'My Account', parent: null };
};